import {
  HeritageBadge, PrimaryButton, GhostButton, WaveDivider,
  PhotoPlaceholder, Eyebrow, TrustStrip, Pill, TicketStub, PolaroidNote
} from '@/components/atoms'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'

const TRUST_ITEMS = [
  "Boulder-based",
  "Expert fit & assembly",
  "Free test rides",
  "Colorado's largest cargo fleet",
]

export default function UiSandbox() {
  return (
    <div className="p-8 max-w-2xl mx-auto flex flex-col gap-8">
      <h1 className="text-2xl font-bold">shadcn UI Sandbox (Story 4.1)</h1>

      <section>
        <h2 className="text-lg font-semibold mb-4">Button</h2>
        <div className="flex gap-2">
          <Button variant="default">Default</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="outline">Outline</Button>
        </div>
      </section>

      <Separator />

      <section>
        <h2 className="text-lg font-semibold mb-4">Card</h2>
        <Card>
          <CardHeader>
            <CardTitle>Front Range Cargo Bikes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground text-sm">
              Quality cargo bikes for Colorado families and commuters.
            </p>
          </CardContent>
        </Card>
      </section>

      <Separator />

      <section>
        <h2 className="text-lg font-semibold mb-4">Accordion</h2>
        <Accordion type="single" collapsible>
          <AccordionItem value="item-1">
            <AccordionTrigger>What makes cargo bikes great?</AccordionTrigger>
            <AccordionContent>
              Cargo bikes replace car trips with clean, human-powered transport — perfect for
              grocery runs, school pickups, and weekend adventures.
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </section>

      <Separator />

      <section>
        <h2 className="text-lg font-semibold mb-4">Badge</h2>
        <div className="flex gap-2">
          <Badge variant="default">In Stock</Badge>
          <Badge variant="secondary">Demo Available</Badge>
          <Badge variant="outline">New Arrival</Badge>
        </div>
      </section>

      <Separator />

      <section>
        <h2 className="text-lg font-semibold mb-4">Input</h2>
        <Input placeholder="Search bikes..." />
      </section>

      {/* ── Epic 15 Atoms ─────────────────────────────────────────────── */}

      <Separator />

      {/* HeritageBadge — default + invert variants (AC 1, 5) */}
      <section>
        <h2 className="text-lg font-semibold mb-4">HeritageBadge</h2>
        <div className="flex flex-col gap-4">
          <div>
            <p className="text-xs text-muted-foreground mb-2">Default</p>
            <HeritageBadge />
          </div>
          <div className="bg-primary p-4 rounded-md">
            <p className="text-xs text-background mb-2">Inverted (on dark background)</p>
            <HeritageBadge invert={true} />
          </div>
        </div>
      </section>

      <Separator />

      {/* Eyebrow (AC 1) */}
      <section>
        <h2 className="text-lg font-semibold mb-4">Eyebrow</h2>
        <Eyebrow>Explore Our Fleet</Eyebrow>
      </section>

      <Separator />

      {/* PrimaryButton — default + hand mode (AC 1, 3) */}
      <section>
        <h2 className="text-lg font-semibold mb-4">PrimaryButton</h2>
        <div className="flex flex-wrap gap-4">
          <div>
            <p className="text-xs text-muted-foreground mb-2">Default</p>
            <PrimaryButton>Shop Cargo Bikes</PrimaryButton>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-2">Hand mode (lg)</p>
            <PrimaryButton hand="Book a demo ride" sub="30 min · Creek Path" size="lg" />
          </div>
        </div>
      </section>

      <Separator />

      {/* GhostButton (AC 1) */}
      <section>
        <h2 className="text-lg font-semibold mb-4">GhostButton</h2>
        <div className="flex gap-4">
          <GhostButton>Learn More</GhostButton>
          <GhostButton sub="No commitment needed">Schedule a Test Ride</GhostButton>
        </div>
      </section>

      <Separator />

      {/* TrustStrip — light + dark variants (AC 1, 4) — full-bleed wrapper */}
      <section>
        <h2 className="text-lg font-semibold mb-4">TrustStrip</h2>
        <div className="flex flex-col gap-4 -mx-8">
          <div>
            <p className="text-xs text-muted-foreground mb-2 mx-8">Light</p>
            <TrustStrip items={TRUST_ITEMS} />
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-2 mx-8">Dark</p>
            <TrustStrip items={TRUST_ITEMS} dark={true} />
          </div>
        </div>
      </section>

      <Separator />

      {/* PhotoPlaceholder — multiple tones + caption (AC 1, 6) */}
      <section>
        <h2 className="text-lg font-semibold mb-4">PhotoPlaceholder</h2>
        <div className="flex flex-col gap-4">
          <div>
            <p className="text-xs text-muted-foreground mb-2">Tone: sunset</p>
            <PhotoPlaceholder tone="sunset" caption="Family cargo ride" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-2">Tone: creek</p>
            <PhotoPlaceholder tone="creek" caption="Boulder Creek path" />
          </div>
        </div>
      </section>

      <Separator />

      {/* PolaroidNote — with rotate prop + children (AC 1, 7) */}
      <section>
        <h2 className="text-lg font-semibold mb-4">PolaroidNote</h2>
        <div className="flex flex-wrap gap-8 py-8">
          <PolaroidNote rotate={2.5} signoff="— Ryan">
            Best cargo bike shop in Colorado. Ryan helped us find the perfect e-bike for school
            runs — our kids love it!
          </PolaroidNote>
          <PolaroidNote rotate={-1.8}>
            Switched from a minivan to a Tern GSD and never looked back. The service team is
            fantastic.
          </PolaroidNote>
        </div>
      </section>

      <Separator />

      {/* WaveDivider — between contrasting color sections (AC 1, 8) */}
      <section>
        <h2 className="text-lg font-semibold mb-4">WaveDivider</h2>
        <div className="-mx-8 overflow-hidden">
          <div className="bg-primary py-8 px-8">
            <p className="text-background text-sm font-medium text-center">Dark section above</p>
          </div>
          <WaveDivider color="var(--background)" />
          <div className="bg-background py-8 px-8">
            <p className="text-foreground text-sm font-medium text-center">Light section below</p>
          </div>
        </div>
      </section>

      <Separator />

      {/* TicketStub — amount + redeemed (AC 1, 9) */}
      <section>
        <h2 className="text-lg font-semibold mb-4">TicketStub</h2>
        <div className="max-w-xs mx-auto">
          {/* default amount derives from CO_TAX_CREDIT — leave unset to use it */}
          <TicketStub redeemed={true} />
        </div>
      </section>

      <Separator />

      {/* Pill — bonus atom (already imported) */}
      <section>
        <h2 className="text-lg font-semibold mb-4">Pill</h2>
        <div className="flex gap-2 flex-wrap">
          <Pill>Electric</Pill>
          <Pill>Cargo</Pill>
          <Pill>Family</Pill>
        </div>
      </section>
    </div>
  )
}
